/*
 * Diálogos y avisos del panel de control con el estilo del dashboard.
 * Se usan con el componente como primer argumento: confirmar(this, "¿Seguro?")
 */
import { notificar } from "./notificaciones.js";

export function confirmar(vm, mensaje, { titulo = "Confirmar", ok = "Aceptar", cancelar = "Cancelar", peligro = false } = {}) {
  return vm.$bvModal.msgBoxConfirm(mensaje, {
    title: titulo,
    size: "sm",
    centered: true,
    modalClass: "db-modal db-dialog",
    okTitle: ok,
    cancelTitle: cancelar,
    okVariant: peligro ? "danger" : "primary",
    cancelVariant: "light",
    footerClass: "db-dialog-footer",
    hideHeaderClose: false,
  });
}

/**
 * Muestra un aviso en la esquina del panel.
 * avisar(this, "Usuario creado.")                       -> éxito
 * avisar(this, "No se pudo…", "danger")                 -> error
 * avisar(this, "…", "danger", "Título", ["detalle 1"])  -> con título y detalles
 * El primer argumento (el componente) se mantiene por compatibilidad.
 */
// eslint-disable-next-line no-unused-vars
export function avisar(vm, mensaje, variant = "success", titulo, detalles) {
  notificar({ tipo: variant, mensaje, titulo, detalles });
}

/**
 * Aviso de error a partir de una excepción de axios: muestra el mensaje de la
 * API y, si hay varios errores de validación, los lista.
 */
export function avisarError(vm, error, porDefecto) {
  notificar({
    tipo: "danger",
    mensaje: mensajeError(error, porDefecto),
    detalles: erroresValidacion(error).slice(1, 4),
  });
}

// Lista de mensajes de validación (422) de Laravel
export function erroresValidacion(error) {
  const errores = error && error.response && error.response.data && error.response.data.errors;
  if (!errores) return [];
  return Object.values(errores).flat().filter(Boolean);
}

// Extrae un mensaje legible de un error de axios
export function mensajeError(error, porDefecto = "Ha ocurrido un error. Inténtalo de nuevo más tarde.") {
  if (!error || !error.response) {
    // Petición enviada sin respuesta: red caída o servidor inaccesible
    if (error && error.code === "ECONNABORTED") return "El servidor ha tardado demasiado en responder. Inténtalo de nuevo.";
    if (error && error.request) return "No hay conexión con el servidor. Comprueba tu conexión a internet.";
    return porDefecto;
  }

  const status = error.response.status;
  const data = error.response.data || {};
  const validacion = erroresValidacion(error);
  if (validacion.length) return validacion[0];

  if (data.message) return data.message;
  if (status === 413) return "El archivo es demasiado grande para el servidor.";
  if (status >= 500) return `${porDefecto} (error del servidor ${status})`;
  return porDefecto;
}

// "2025-03-05 10:00:00" -> Date local (new Date() falla con el espacio en Safari)
export function parseFecha(valor) {
  if (!valor) return null;
  if (valor instanceof Date) return valor;
  const m = String(valor).match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{2}):(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
  const d = new Date(valor);
  return isNaN(d) ? null : d;
}

export function formatFecha(valor, { corta = false } = {}) {
  const d = parseFecha(valor);
  if (!d) return "—";
  return d.toLocaleDateString("es-ES", corta ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
}

// Date -> "YYYY-MM-DD" (para <input type="date">)
export function toISODate(d) {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function normalizar(texto) {
  return (texto || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Permisos que devuelve la API (mismo catálogo que App\Support\Permisos en el backend)
export const PERMISOS = {
  NOTICIAS: "noticias.gestionar",
  CARITAS: "caritas.gestionar",
  GALERIAS: "galerias.gestionar",
  POPUPS: "popups.gestionar",
  ENLACES: "enlaces.gestionar",
  HORARIOS: "horarios.gestionar",
  USUARIOS: "usuarios.gestionar",
  ROLES: "roles.gestionar",
};

export function iniciales(nombre) {
  return (nombre || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
}

export function formatFechaHora(valor) {
  if (!valor) return "—";
  const d = new Date(valor);
  if (isNaN(d)) return "—";
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Requisitos de contraseña: los mismos que valida el backend (Password::defaults)
export function requisitosPassword(password) {
  const pw = password || "";
  return [
    { texto: "Al menos 12 caracteres", ok: pw.length >= 12 },
    { texto: "Mayúsculas y minúsculas", ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw) },
    { texto: "Al menos un número", ok: /\d/.test(pw) },
  ];
}

export function passwordValida(password) {
  return requisitosPassword(password).every((r) => r.ok) && (password || "").length <= 128;
}

// Contraseña aleatoria que siempre cumple la política
export function generarPassword(longitud = 16) {
  const grupos = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnpqrstuvwxyz", "23456789", "!@#$%*-_=+?"];
  const todos = grupos.join("");
  const aleatorio = (max) => {
    const r = new Uint32Array(1);
    window.crypto.getRandomValues(r);
    return r[0] % max;
  };
  const chars = grupos.map((g) => g.charAt(aleatorio(g.length)));
  while (chars.length < longitud) chars.push(todos.charAt(aleatorio(todos.length)));
  // Mezcla (Fisher-Yates) para que los obligatorios no queden al principio
  for (let i = chars.length - 1; i > 0; i--) {
    const j = aleatorio(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
